/**
 * 预警条目组件 — 对齐视觉基线 .alert（design/assets/theme.css §预警流）。
 *
 * 结构：左 3px 语义色条（P0 error / P1 warning）+ 级别 Badge + 标题（粗）
 * + 摘要（超 1 行可展开/收起）+ 最近触发时间 + 钻取链接 + 标记处理按钮。
 *
 * 标记处理：POST /api/alerts/{id}/resolve，成功后本地置 resolved 样式弱化
 * （透明度降低、色条转中性、按钮消失），并回调 onResolved 让父级同步计数。
 */
import { useState } from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Badge from "./Badge";
import { resolveAlert } from "../api/alerts";
import { formatDateTime } from "../utils/format";
import type { AlertItem as AlertItemData } from "../types";

export interface AlertItemProps {
  /** 预警事件（契约见 kickoff 02 §6）。 */
  item: AlertItemData;
  /** 标记处理成功后回调（父级更新列表状态与汇总计数）。 */
  onResolved?: (id: string) => void;
  /** 演示站模式：标记处理按钮置灰（提示"演示模式"，不发请求）。 */
  demoMode?: boolean;
}

/** 钻取页路径 → 链接文案（对齐概念稿"看矩阵/看竞品/看台账"）。 */
const DRILLDOWN_LABEL: Record<string, string> = {
  "/ai-visibility": "看矩阵",
  "/competitive": "看竞品",
  "/index-ledger": "看台账",
  "/citations": "看引用源",
  "/citation-sources": "看引用源",
};

/** 由 drilldown 拼路由跳转目标（page + query params）。 */
function drilldownTo(item: AlertItemData): string | null {
  const d = item.drilldown;
  if (!d || !d.page) return null;
  const qs = d.params ? new URLSearchParams(d.params).toString() : "";
  return qs ? `${d.page}?${qs}` : d.page;
}

export default function AlertItem({ item, onResolved, demoMode = false }: AlertItemProps) {
  // 本地 resolved 覆盖：POST 成功后立即弱化，不等父级刷新
  const [resolvedLocal, setResolvedLocal] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState("");
  const [expanded, setExpanded] = useState(false);

  const resolved = resolvedLocal || item.status === "resolved";
  const isP0 = item.level === "P0";
  const to = drilldownTo(item);

  /** 标记处理：成功后本地弱化 + 回调父级；失败内联提示，不抛红屏。 */
  const handleResolve = async () => {
    setResolving(true);
    setResolveError("");
    try {
      await resolveAlert(item.id);
      setResolvedLocal(true);
      onResolved?.(item.id);
    } catch (err) {
      setResolveError(err instanceof Error ? err.message : "操作失败，请重试");
    } finally {
      setResolving(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        px: 2,
        py: 1.5,
        border: "1px solid",
        borderColor: "divider",
        // 左 3px 语义色条（对齐 .alert.p0/.p1；resolved 转中性）
        borderLeft: "3px solid",
        borderLeftColor: resolved
          ? "text.disabled"
          : isP0
            ? "error.main"
            : "warning.main",
        borderRadius: 2,
        bgcolor: "background.paper",
        opacity: resolved ? 0.55 : 1,
        transition: "opacity .2s",
      }}
    >
      {resolved ? (
        <Badge label="已处理" variant="default" />
      ) : (
        <Badge label={item.level} variant={isP0 ? "error" : "warning"} />
      )}

      {/* 标题 + 摘要（可展开） */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            color: "text.secondary",
            ...(expanded
              ? {}
              : {
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 1,
                }),
          }}
        >
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
            {item.title}
          </Box>
          {item.summary && `——${item.summary}`}
        </Typography>
        {item.summary && item.summary.length > 40 && (
          <Link
            component="button"
            onClick={() => setExpanded((v) => !v)}
            sx={{ fontSize: 11, color: "primary.main", mt: 0.25, p: 0 }}
            underline="hover"
          >
            {expanded ? "收起" : "展开"}
          </Link>
        )}
      </Box>

      {/* 最近触发时间 */}
      <Typography
        sx={{ fontSize: 12, color: "text.disabled", whiteSpace: "nowrap" }}
      >
        {formatDateTime(item.last_triggered_at)}
      </Typography>

      {/* 操作区：钻取链接 + 标记处理 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          whiteSpace: "nowrap",
        }}
      >
        {to && (
          <Link
            component={RouterLink}
            to={to}
            sx={{ fontSize: 12, color: "primary.main", textDecoration: "none" }}
          >
            {DRILLDOWN_LABEL[item.drilldown?.page || ""] || "去查看"}
          </Link>
        )}
        {!resolved && demoMode && (
          <Button
            size="small"
            variant="outlined"
            disabled
            title="演示模式"
            sx={{ fontSize: 12 }}
          >
            标记处理
          </Button>
        )}
        {!resolved && !demoMode && (
          <Button
            size="small"
            variant="outlined"
            disabled={resolving}
            onClick={handleResolve}
            sx={{ fontSize: 12 }}
          >
            {resolving ? "处理中…" : "标记处理"}
          </Button>
        )}
        {resolveError && (
          <Typography sx={{ fontSize: 11, color: "error.main" }}>
            {resolveError}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
